/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { EnduranceRBACGroupsEnum } from '../enums/api/endurance-rbac-groups.enums';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { RbacAddGroupModel } from '../models/rbac-addGroup.model';
import { RbacDeleteGroupModel } from '../models/rbac-deleteGroup.model';
import { RbacEditGroupModel } from '../models/rbac-editGroup.model';
import { RbacGetGroupByIdModel } from '../models/rbac-getGroupById.model';

@Injectable()
export class EnduranceRBACGroupsApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService,
    ) {
    }

    /**
     * Create a group using the RBAC system on the API.
     * @param {string} groupObject
     * @returns {any}
     */
    public edit(groupObject: RbacEditGroupModel): Observable<RbacEditGroupModel[]> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.put(EnduranceRBACGroupsEnum.PUT_GROUPS_EDIT(groupObject.id), groupObject, moduleAuth, false)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Create a group using the RBAC system on the API.
     * @param {string} groupObject
     * @returns {any}
     */
    public add(groupObject: any): Observable<RbacAddGroupModel[]> {
        return new Observable((observer): void => {
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.post(EnduranceRBACGroupsEnum.POST_GROUPS_ADD, groupObject, moduleAuth, false)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Delete a group using the RBAC system on the API.
     * @param {string} groupId
     * @returns {any}
     */
    public deleteGroup(groupId: string): Observable<RbacDeleteGroupModel[]> {
        return new Observable((observer): void => {
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.delete(EnduranceRBACGroupsEnum.DELETE_GROUPS(groupId), moduleAuth, false)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Get all groups using the RBAC system on the API.
     * @returns {any}
     */
    public getAllGroups(): Observable<any> {
        return new Observable((observer): void => {
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.get(EnduranceRBACGroupsEnum.GET_GROUPS_ALL, moduleAuth, false)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Get a groups using the RBAC system on the API.
     * @param {string} groupId
     * @returns {any}
     */
    public getGroupById(groupId: string): Observable<RbacGetGroupByIdModel> {
        return new Observable((observer): void => {
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.get(EnduranceRBACGroupsEnum.GET_GROUPS_ID(groupId), moduleAuth, false)
                .subscribe(
                    (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
        });
    }
}
